/**
 * The booking form asks for a website as free text, so what a lead types is
 * usually schemeless ("acmecorps.com") and occasionally not a site at all.
 * Sending that through verbatim stores a string nobody can click.
 *
 * `normalizeWebsite` returns a canonical absolute URL, or `undefined` when the
 * input could not be one — callers treat `undefined` on non-empty input as a
 * validation failure.
 */
export function normalizeWebsite(raw: string): string | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;

  const withScheme = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;

  try {
    const url = new URL(withScheme);
    // "acme" parses as a URL but is not a reachable host; requiring a dot in
    // the hostname is what separates a typo from a domain. A trailing dot is
    // syntactically legal but never what someone meant to type.
    if (!url.hostname.includes(".") || url.hostname.endsWith(".")) {
      return undefined;
    }
    if (url.hostname.startsWith(".")) return undefined;
    return url.toString();
  } catch {
    return undefined;
  }
}

/** Empty is valid — the website field is optional. */
export function isValidWebsite(raw: string): boolean {
  return !raw.trim() || normalizeWebsite(raw) !== undefined;
}
